import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { api } from '../utils';

export const CustomerAuth = ({ onAuthSuccess }) => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/public/branches').then(setBranches).catch(console.error);
  }, []);

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const check = await api.post('/customer/auth/email/check', { email });
      setIsNewUser(check.is_new_user);
      await api.post('/customer/auth/email/send-otp', { email });
      setStep(2);
    } catch (err) {
      setError(err.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isNewUser) {
        setStep(3);
      } else {
        const res = await api.post('/customer/auth/email/verify-otp', { email, otp });
        onAuthSuccess(res.access_token, 'customer');
      }
    } catch (err) {
      setError(err.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/customer/auth/register', { email, name, mobile, branch_id: parseInt(branchId) });
      const res = await api.post('/customer/auth/email/verify-otp', { email, otp });
      onAuthSuccess(res.access_token, 'customer');
    } catch (err) {
      setError(err.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-600 to-violet-600"></div>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900">
            {step === 1 ? 'Welcome Back' : step === 2 ? 'Verify OTP' : 'Complete Profile'}
          </h2>
          <p className="text-gray-500 mt-2">
            {step === 1 ? 'Enter your email to continue' : step === 2 ? `OTP sent to ${email}` : 'Tell us a bit about yourself'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl text-sm flex items-start gap-2 border border-red-100">
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleEmailCheck} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" />
            </div>
            <button disabled={loading} className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex justify-center gap-2">
              {loading ? <RefreshCw className="animate-spin" size={20} /> : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleOtpVerify} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">6-Digit OTP</label>
              <input type="text" required value={otp} onChange={e => setOtp(e.target.value)} placeholder="000000" maxLength={6} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center tracking-widest text-2xl font-bold outline-none" />
            </div>
            <button disabled={loading} className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex justify-center gap-2">
              {loading ? <RefreshCw className="animate-spin" size={20} /> : 'Verify & Continue'}
            </button>
            <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-gray-500 font-medium">Use different email</button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div><label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label><input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" /></div>
            <div><label className="block text-sm font-semibold text-gray-700 mb-1">Mobile</label><input type="tel" required value={mobile} onChange={e => setMobile(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" /></div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Select Branch</label>
              <select required value={branchId} onChange={e => setBranchId(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none">
                <option value="">Choose nearest branch...</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <button disabled={loading} className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl flex justify-center gap-2 mt-4">
              {loading ? <RefreshCw className="animate-spin" size={20} /> : 'Complete Registration'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const AdminLogin = ({ onAuthSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/admin/login', { email, password });
      onAuthSuccess(res.access_token, 'admin');
    } catch (err) {
      setError(err.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-block p-3 bg-indigo-100 text-indigo-600 rounded-2xl mb-4"><ShieldCheck size={32} /></div>
          <h2 className="text-3xl font-bold text-gray-900">Admin Login</h2>
        </div>

        {error && <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl text-sm flex gap-2"><AlertCircle size={18} /><span>{error}</span></div>}

        <form onSubmit={handleLogin} className="space-y-6">
          <div><label className="block text-sm font-semibold text-gray-700 mb-2">Admin Email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" /></div>
          <div><label className="block text-sm font-semibold text-gray-700 mb-2">Password</label><input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" /></div>
          <button disabled={loading} className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl flex justify-center gap-2">
            {loading ? <RefreshCw className="animate-spin" size={20} /> : 'Login to Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
};
